const DEFAULT_C15T_BACKEND_URL = "https://514-ai-dev-fiveonefour.inth.app";

export const C15T_BACKEND_URL =
  process.env.NEXT_PUBLIC_C15T_BACKEND_URL || DEFAULT_C15T_BACKEND_URL;

export const C15T_BROWSER_BACKEND_URL = process.env.NEXT_PUBLIC_C15T_BACKEND_URL || "/c15t";

export const C15T_STORAGE_KEY = "axp-consent";

export const C15T_COOKIE_DOMAIN = process.env.NEXT_PUBLIC_C15T_COOKIE_DOMAIN || undefined;

/** ISO 3166-1 alpha-2 country that selects c15t's EU opt-in / banner policy. */
export const DEFAULT_NON_PRODUCTION_CONSENT_COUNTRY = "DE";

const AUTO_CONSENT_JURISDICTION = "auto";

export type ConsentJurisdictionEnv = {
  NODE_ENV?: string;
  VERCEL_ENV?: string;
  NEXT_PUBLIC_C15T_JURISDICTION_COUNTRY?: string;
};

/**
 * Named `process.env.VAR` reads so Next.js inlines them into client bundles.
 * Passing the whole `process.env` object is empty in the browser, which would
 * treat production as non-production and force `{ country: "DE" }`.
 *
 * `NEXT_PUBLIC_VERCEL_ENV` is the client-visible copy of `VERCEL_ENV`.
 */
export function processConsentJurisdictionEnv(): ConsentJurisdictionEnv {
  return {
    NODE_ENV: process.env.NODE_ENV,
    VERCEL_ENV: process.env.VERCEL_ENV || process.env.NEXT_PUBLIC_VERCEL_ENV,
    NEXT_PUBLIC_C15T_JURISDICTION_COUNTRY: process.env.NEXT_PUBLIC_C15T_JURISDICTION_COUNTRY,
  };
}

/**
 * Production deployments must keep real geo resolution. Preview and local
 * `next dev` are non-production; `next start` without VERCEL_ENV follows
 * NODE_ENV, matching `deploymentEnvironment()` in datadog-rum-context.
 */
export function isProductionConsentDeployment(
  env: ConsentJurisdictionEnv = processConsentJurisdictionEnv(),
): boolean {
  if (env.VERCEL_ENV === "production") return true;
  if (env.VERCEL_ENV === "preview" || env.VERCEL_ENV === "development") return false;
  return env.NODE_ENV === "production";
}

/**
 * Country code passed to c15t as `overrides.country` so jurisdiction is
 * deterministic. Inert in production even if the env var is set.
 *
 * Non-production: unset → `DE` (consent-required banner); `auto` → real geo;
 * any other value → that ISO country code, uppercased.
 */
export function pinnedConsentJurisdictionCountry(
  env: ConsentJurisdictionEnv = processConsentJurisdictionEnv(),
): string | undefined {
  if (isProductionConsentDeployment(env)) return undefined;

  const raw = env.NEXT_PUBLIC_C15T_JURISDICTION_COUNTRY?.trim();
  if (!raw) return DEFAULT_NON_PRODUCTION_CONSENT_COUNTRY;
  if (raw.toLowerCase() === AUTO_CONSENT_JURISDICTION) return undefined;
  return raw.toUpperCase();
}

export function consentJurisdictionOverrides(
  env: ConsentJurisdictionEnv = processConsentJurisdictionEnv(),
): { country: string } | undefined {
  const country = pinnedConsentJurisdictionCountry(env);
  return country ? { country } : undefined;
}

interface HeaderReader {
  get(name: string): string | null;
}

export function cookieDomainForHostname(
  configuredDomain: string | undefined,
  hostname: string,
): string | undefined {
  const domain = configuredDomain?.trim();
  if (!domain) return undefined;

  const normalizedDomain = domain.replace(/^\./, "");
  return hostname === normalizedDomain || hostname.endsWith(`.${normalizedDomain}`)
    ? domain
    : undefined;
}

export function cookieDomainForRequestHeaders(
  configuredDomain: string | undefined,
  requestHeaders: HeaderReader,
): string | undefined {
  const forwardedHost = requestHeaders.get("x-forwarded-host")?.split(",", 1)[0]?.trim();
  const host = forwardedHost || requestHeaders.get("host")?.trim();
  if (!host) return undefined;

  try {
    return cookieDomainForHostname(configuredDomain, new URL(`https://${host}`).hostname);
  } catch {
    return undefined;
  }
}

export const C15T_CONSENT_CATEGORIES = ["necessary", "measurement", "marketing"] as const;

// Kept accurate as capture moves first-party (ENG-4281): pages viewed and
// in-product actions are now recorded on 514's own servers rather than by a
// third-party analytics SDK, while PostHog still receives product analytics
// for the remainder of the PostHog migration.
export const C15T_MEASUREMENT_DESCRIPTION =
  "Measurement helps us understand product usage. Pages you view and actions you take are recorded on our own servers, and product analytics are also sent to PostHog. The ax_ft cookie stores a random correlation ID, landing page, referrer, UTM source, medium, campaign, term, content, and the first-seen time for up to one year.";

export const C15T_CONSENT_DIALOG_DESCRIPTION =
  "Choose whether we may measure product usage with our own analytics, PostHog, and the ax_ft first-touch cookie. Necessary cookies remain enabled.";
